import { useTranslate } from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import { Card, Flex, Progress, Table, Tag, Tooltip, Typography } from 'antd';
import type {
  CycleCloseReason,
  CycleKind,
  CycleListOut,
} from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash } from '@/components/common/show/shared';
import { CycleKindTag, CycleStatusTag } from '@/components/settlement/tags';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { usePublicationsByProducts } from '@/components/user_catalog/publications/usePublicationsByProducts';
import { formatDate, formatFraction, formatSecondsClock } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';

export const ProfileCyclesTab = ({ profileId }: { profileId: number }) => {
  const t = useTranslate();
  const { tableProps, sorters, result } = useTable<CycleListOut>({
    resource: 'settlement.cycles',
    queryOptions: { enabled: profileId != null },
    filters: {
      permanent: [{ field: 'profile_id', operator: 'eq', value: profileId }],
    },
    // covered_* are generated and unindexed server-side; the PK stays cheap.
    sorters: { initial: [{ field: 'id', order: 'desc' }] },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });

  const productIds = [
    ...new Set((result?.data ?? []).map((r) => r.product_id).filter(Boolean)),
  ];
  const publicationByProduct = usePublicationsByProducts(productIds);

  return (
    <Card
      title={`${t('account.profiles.tabs.cycles')} (${result?.total ?? 0})`}
    >
      <Table<CycleListOut>
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
        size="small"
      >
        <Table.Column<CycleListOut>
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowIconLink resource="settlement.cycles" id={value}>
              {value}
            </ShowIconLink>
          )}
        />
        <Table.Column<CycleListOut>
          dataIndex="product_id"
          title={t('settlement.cycles.fields.product_id')}
          render={(value: number, record) => {
            const publication = publicationByProduct.get(value);
            return orDash(
              value != null && (
                <Flex gap={4} align="center" wrap>
                  {publication ? (
                    <PublicationInfoPopover
                      publication={publication}
                      publicationId={publication.id}
                    />
                  ) : (
                    value
                  )}
                  <Typography.Text type="secondary">
                    {`v${record.product_version}`}
                  </Typography.Text>
                </Flex>
              )
            );
          }}
        />
        <Table.Column<CycleListOut>
          dataIndex="pass_number"
          title={t('settlement.cycles.fields.pass_number')}
          sorter
          sortOrder={getSortOrder(sorters, 'pass_number')}
          render={(value: number) => orDash(value)}
        />
        <Table.Column<CycleListOut>
          dataIndex="cycle_kind"
          title={t('settlement.cycles.fields.cycle_kind')}
          render={(value: CycleKind) =>
            orDash(value && <CycleKindTag value={value} />)
          }
        />
        <Table.Column<CycleListOut>
          dataIndex="started_at"
          title={t('settlement.cycles.fields.started_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'started_at')}
          render={(value: string) => orDash(formatDate(value))}
        />
        <Table.Column<CycleListOut>
          dataIndex="expires_at"
          title={t('settlement.cycles.fields.expires_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'expires_at')}
          // Null: no min-threshold crossing yet, so the window has no end.
          render={(value: string | null) => orDash(formatDate(value))}
        />
        <Table.Column<CycleListOut>
          dataIndex="covered_fraction"
          title={t('settlement.cycles.fields.coverage')}
          sorter
          sortOrder={getSortOrder(sorters, 'covered_fraction')}
          render={(value: string, record) => (
            <Flex vertical gap={2} style={{ minWidth: 130 }}>
              <Tooltip title={value}>
                <Progress
                  percent={Math.round(Number(value) * 100)}
                  format={() => formatFraction(value)}
                  size="small"
                  style={{ marginBottom: 0 }}
                />
              </Tooltip>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {formatSecondsClock(record.covered_seconds, {
                  alwaysHours: true,
                })}
              </Typography.Text>
            </Flex>
          )}
        />
        <Table.Column<CycleListOut>
          dataIndex="is_billable"
          title={t('settlement.cycles.fields.is_billable')}
          render={(value: boolean) => (
            <Tag color={value ? 'green' : 'default'}>
              {t(value ? 'common.yes' : 'common.no')}
            </Tag>
          )}
        />
        <Table.Column<CycleListOut>
          dataIndex="close_reason"
          title={t('settlement.cycles.fields.status')}
          render={(value: CycleCloseReason | null, record) => (
            <CycleStatusTag status={record.status} closeReason={value} />
          )}
        />
      </Table>
    </Card>
  );
};
